/**
 * clusterUtils.ts
 * Utilities for handling cluster questions properly
 * Ensures cluster questions stay together and cannot be shuffled separately
 */

// Local type definitions to avoid circular dependencies
// Using string for type to avoid dependency on THPT-specific types
export interface ClusterSubQuestion {
  id: string;
  type: string;
  title?: string;
  context?: string;
  stem?: string;
  points: number;
  options?: Array<string | { id: string; content: string }>;
  subject?: string;
}

export interface DemoQuestion {
  id: string;
  type:
    | string
    | 'mc'
    | 'tf'
    | 'short'
    | 'short_text'
    | 'matching'
    | 'trueFalse'
    | 'cluster'
    | 'essay'
    | 'fillBlank';
  title?: string;
  stem?: string;
  context?: string;
  subject?: string;
  category?: string;
  points: number;
  options?: Array<string | { id: string; content: string }>;
  clusterQuestions?: ClusterSubQuestion[];
  clusterContext?: {
    clusterId: string;
    title: string;
    content: string;
  };
  clusterId?: string;
  clusterQuestionCount?: number; // Total questions in this question cluster (for cloze)
  clusterQuestionIndex?: number; // Index of this question in the cluster (0-based)
  _clusterParentId?: string;
  _clusterIndex?: number;
}

/**
 * Expands cluster questions into individual renderable questions
 * while maintaining cluster integrity for shuffling
 */
export function expandClusterQuestions(questions: DemoQuestion[]): DemoQuestion[] {
  const expanded: DemoQuestion[] = [];

  for (const question of questions) {
    if (!question) {
      expanded.push(null as any);
      continue;
    }

    if (question.type === 'cluster' && question.clusterQuestions) {
      // Convert cluster sub-questions to full questions with shared context
      for (const subQuestion of question.clusterQuestions) {
        const expandedQuestion: DemoQuestion = {
          ...subQuestion,
          id: subQuestion.id,
          type: subQuestion.type as string,
          stem: subQuestion.stem || '', // Question prompt text
          context: subQuestion.context, // Shared passage/reading material (e.g., poem)
          points: subQuestion.points,
          clusterContext: question.clusterContext,
          clusterId: question.clusterId,
          // Mark as part of cluster to prevent individual shuffling
          _clusterParentId: question.id,
          _clusterIndex: question.clusterQuestions.indexOf(subQuestion),
        };
        expanded.push(expandedQuestion);
      }
    } else {
      // Regular question - add as is
      expanded.push(question);
    }
  }

  return expanded;
}

/**
 * Groups questions by cluster for proper shuffling
 * Cluster questions are treated as single units
 */
export function groupQuestionsForShuffling(
  questions: DemoQuestion[]
): (DemoQuestion | DemoQuestion[])[] {
  const groups: (DemoQuestion | DemoQuestion[])[] = [];
  const clusterGroups = new Map<string, DemoQuestion[]>();

  for (const question of questions) {
    if (!question) {
      groups.push(null as any);
      continue;
    }
    if (question._clusterParentId) {
      // This is part of a cluster
      if (!clusterGroups.has(question._clusterParentId)) {
        clusterGroups.set(question._clusterParentId, []);
      }
      clusterGroups.get(question._clusterParentId)!.push(question);
    } else {
      // Regular standalone question
      groups.push(question);
    }
  }

  // Add cluster groups
  for (const clusterQuestions of clusterGroups.values()) {
    // Sort by cluster index to maintain order within cluster
    clusterQuestions.sort((a, b) => (a._clusterIndex || 0) - (b._clusterIndex || 0));
    groups.push(clusterQuestions);
  }

  return groups;
}

/**
 * Shuffles questions while keeping clusters together
 */
export function shuffleQuestionsWithClusters(questions: DemoQuestion[]): DemoQuestion[] {
  const expanded = expandClusterQuestions(questions);
  const groups = groupQuestionsForShuffling(expanded);

  // Shuffle the groups (not individual questions within clusters)
  const shuffledGroups = [...groups].sort(() => Math.random() - 0.5);

  // Flatten back to question array
  const result: DemoQuestion[] = [];
  for (const group of shuffledGroups) {
    if (Array.isArray(group)) {
      // Cluster questions - add in order
      result.push(...group);
    } else {
      // Single question
      result.push(group);
    }
  }

  return result;
}

/**
 * Validates cluster question structure
 */
export function validateClusterQuestions(questions: DemoQuestion[]): string[] {
  const errors: string[] = [];

  for (const question of questions) {
    if (!question) continue;
    if (question.type === 'cluster') {
      if (!question.clusterQuestions || question.clusterQuestions.length === 0) {
        errors.push(`Cluster question ${question.id} has no sub-questions`);
      }

      if (!question.clusterContext) {
        errors.push(`Cluster question ${question.id} has no context`);
      }

      if (question.stem) {
        errors.push(`Cluster question ${question.id} should not have a stem - context only`);
      }
    }
  }

  return errors;
}

/**
 * Extended ClusterContext type that supports multiple sub-clusters
 * Used when a cluster contains questions from different question clusters
 */
export interface ExtendedClusterContext {
  clusterId: string;
  title?: string;
  content?: string;
  subject?: string;
  mediaType?: string;
  /** Single stimulus data for clusters with one sub-cluster */
  stimulusData?: {
    stimulusText?: string;
    content?: string;
    questionClusterId?: string;
  };
  /** Map of stimulus data keyed by questionClusterId for clusters with multiple sub-clusters */
  stimulusDataMap?: Record<
    string,
    {
      stimulusText?: string;
      content?: string;
    }
  >;
  /** True if this cluster has questions from multiple question clusters */
  hasMultipleSubClusters?: boolean;
}

/**
 * Get the correct stimulus data for a question, handling multiple sub-clusters
 *
 * @param question - The question to get stimulus data for
 * @param clusterContext - The cluster context (may have stimulusData or stimulusDataMap)
 * @returns The stimulus data for this specific question, or undefined
 */
export function getStimulusDataForQuestion(
  question: { questionClusterId?: string },
  clusterContext: ExtendedClusterContext | undefined
): { stimulusText?: string; content?: string } | undefined {
  if (!clusterContext) return undefined;

  // Check if this cluster has multiple sub-clusters with different stimulus data
  if (clusterContext.hasMultipleSubClusters && clusterContext.stimulusDataMap) {
    const questionClusterId = question.questionClusterId;

    // CRITICAL FIX: Only return stimulus if ID matches. Do NOT fall back.
    if (questionClusterId && clusterContext.stimulusDataMap[questionClusterId]) {
      return clusterContext.stimulusDataMap[questionClusterId];
    }
    return undefined;
  }

  // Single sub-cluster or no sub-clusters - use stimulusData directly
  const stimulusData = clusterContext.stimulusData;
  if (!stimulusData) return undefined;

  // CRITICAL FIX: If stimulusData is specific to a sub-cluster, only return it if:
  // (a) Question has no questionClusterId, OR (b) It matches.
  if (typeof stimulusData === 'object' && stimulusData !== null) {
    const stimulusOwnerClusterId = (stimulusData as any).questionClusterId;
    if (stimulusOwnerClusterId) {
      const questionClusterId = question.questionClusterId;
      if (!questionClusterId || questionClusterId !== stimulusOwnerClusterId) {
        return undefined;
      }
    }
  }

  return stimulusData;
}

/**
 * Check if a cluster context has multiple sub-clusters with different stimulus data
 */
export function hasMultipleSubClusters(
  clusterContext: ExtendedClusterContext | undefined
): boolean {
  return !!(clusterContext?.hasMultipleSubClusters && clusterContext.stimulusDataMap);
}

/**
 * Transform cluster context from backend format to include proper stimulus handling
 * Ensures consistent structure for frontend consumption
 */
export function normalizeClusterContext(rawContext: any): ExtendedClusterContext {
  if (!rawContext) {
    return { clusterId: '' };
  }

  const normalized: ExtendedClusterContext = {
    clusterId: rawContext.clusterId || rawContext.id || '',
    title: rawContext.title,
    content: rawContext.content,
    subject: rawContext.subject,
    mediaType: rawContext.mediaType,
  };

  // Handle multiple sub-clusters
  if (rawContext.hasMultipleSubClusters && rawContext.stimulusDataMap) {
    normalized.hasMultipleSubClusters = true;
    normalized.stimulusDataMap = rawContext.stimulusDataMap;
  } else if (rawContext.stimulusData) {
    // Single stimulus data
    normalized.stimulusData = rawContext.stimulusData;
  }

  return normalized;
}
